import axios from "axios";

const API_URL = process.env.REACT_APP_UNJABBED;

export const apiServices = axios.create({
	baseURL: API_URL,
	headers: {
		"Access-Control-Allow-Origin": "*",
		"Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
		"x-client-platform": "web",
	},
});

export const setToken = (token: string) => {
	apiServices.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};
export const deleteToken = () => {
	delete apiServices.defaults.headers.common["Authorization"];
};

export const DELETED_ACCOUNT_SESSION_MESSAGE =
	"This account has been deleted or is no longer available.";
export const AUTH_SESSION_MESSAGE_KEY = "unjabbedAuthSessionMessage";

export const saveAuthSessionMessage = (message: string) => {
	if (typeof window === "undefined") return;
	const safeMessage = message.trim();
	if (!safeMessage) return;
	window.sessionStorage.setItem(AUTH_SESSION_MESSAGE_KEY, safeMessage);
};

export const consumeAuthSessionMessage = () => {
	if (typeof window === "undefined") return "";
	const message = window.sessionStorage.getItem(AUTH_SESSION_MESSAGE_KEY) || "";
	window.sessionStorage.removeItem(AUTH_SESSION_MESSAGE_KEY);
	return message.trim();
};

export const getApiErrorMessage = (
	error: any,
	fallback = "Something went wrong. Please try again later."
) => {
	const responseMessage =
		error?.response?.data?.message ??
		error?.response?.data?.error ??
		error?.data?.message ??
		error?.message;

	if (typeof responseMessage === "string" && responseMessage.trim()) {
		return responseMessage.trim();
	}

	return fallback;
};

export const shouldTreatSessionAsExpired = (
	statusCode?: number,
	message = "",
) => {
	const normalizedMessage = message.trim().toLowerCase();

	if (statusCode === 401) {
		return true;
	}

	if (!normalizedMessage) {
		return false;
	}

	if (statusCode === 403) {
		return (
			normalizedMessage.includes("inactive") ||
			normalizedMessage.includes("unauthorized") ||
			normalizedMessage.includes("forbidden") ||
			normalizedMessage.includes("banned")
		);
	}

	if (statusCode === 404) {
		return (
			normalizedMessage.includes("user not found") ||
			normalizedMessage.includes("account not found") ||
			normalizedMessage.includes("account does not exist")
		);
	}

	if (statusCode === 400) {
		return (
			normalizedMessage.includes("inactive") ||
			normalizedMessage.includes("deleted") ||
			normalizedMessage.includes("account does not exist") ||
			normalizedMessage.includes("unauthorized")
		);
	}

	return false;
};

export const createUserVisibleError = (message: string) => {
	const error = new Error(message) as Error & {
		preserveUserMessage?: boolean;
	};
	error.preserveUserMessage = true;
	return error;
};

const redirectToLoginForExpiredSession = () => {
	if (typeof window === "undefined") return;
	window.localStorage.clear();
	deleteToken();
	saveAuthSessionMessage(DELETED_ACCOUNT_SESSION_MESSAGE);
	if (!window.location.pathname.startsWith("/auth/login")) {
		window.location.href = "/auth/login";
	}
};

const excludedRoutes = [
	"/auth/login",
	"/auth/send-otp",
	"/profile/genders",
	"/profile/sexual-orientations",
	"/profile/verification-photo",
	"/account-recovery",
];

apiServices.interceptors.response.use(
	(response) => response,
	(error) => {
		const { response, config } = error;
		if (response) {
			const isExcluded = excludedRoutes.some((route) => config.url?.includes(route));
			const message = getApiErrorMessage(error, "");
			if (!isExcluded && shouldTreatSessionAsExpired(response.status, message)) {
				redirectToLoginForExpiredSession();
			}
		}
		return Promise.reject(error);
	}
);
